import { useEffect, useState } from "react";
import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Badge,
  Card,
  CardBody,
  Col,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import LineItemsTab from "./LineItemsTab";
import AssignmentsOverviewCard from "features/WorkAssignments/components/AssignmentsOverviewCard";
import { WorkAssignmentSourceType } from "features/WorkAssignments/constants/work-assignment.constants";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import {
  WorkOrderStatus,
  workOrderStatusMap,
} from "features/WorkOrders/constants/work-order.constants";
import { Link } from "react-router-dom";
import { DateTime, DateOnly } from "components/common/Date";
import { formatCurrency } from "utils/currency";
import { useWorkOrderOverview } from "./WorkOrderOverviewContext";
import type {
  WorkOrder,
  WorkOrderFinancials,
} from "features/WorkOrders/types/work-order.types";
import { getWorkOrderFinancials } from "features/WorkOrders/services/WorkOrderService";
import { getInvoices } from "features/Invoices/services/InvoiceService";
import {
  InvoiceSourceType,
  InvoiceStatus,
  InvoiceStatusMap,
} from "features/Invoices/constants/invoice.constants";
import type { Invoice } from "features/Invoices/types/invoice.types";
import {
  Timeline,
  type TimelineStep,
  type TimelineStepState,
} from "components/common/Timeline";
import ClientAdvancesCard from "features/ClientAdvances/components/ClientAdvancesCard";
import { ClientAdvanceSourceType } from "features/ClientAdvances/constants/client-advance.constants";
import { getClientDisplayName } from "features/Clients/utils/clientDisplayName";
import { ClientPopover } from "components/common/ClientPopover";

function isTerminalWorkOrderStatus(status: string | null | undefined): boolean {
  const s = String(status ?? "")
    .trim()
    .toUpperCase();
  return s === WorkOrderStatus.COMPLETED || s === WorkOrderStatus.CANCELLED;
}

export default function OverviewTab() {
  const { workOrder, refetch } = useWorkOrderOverview();
  const { can } = useAccess();
  const canCreateAssignment = can(Permissions.WORK_ASSIGNMENT.CREATE);
  const canUpdateAssignment = can(Permissions.WORK_ASSIGNMENT.UPDATE);
  const [lineItemsOpen, setLineItemsOpen] = useState("line-items");
  const toggleLineItems = (id: string) =>
    setLineItemsOpen((prev) => (prev === id ? "" : id));

  if (!workOrder) {
    return null;
  }

  return (
    <>
      <Row className="mb-0">
        <Col lg={8}>
          <Card className="mb-4">
            <CardBody>
              <div className="text-muted">
                <h6 className="mb-3 text-uppercase">Work order info</h6>
                <div className="table-responsive">
                  <Table className="table-borderless mb-0">
                    <tbody>
                      {workOrder.code ? (
                        <tr>
                          <th
                            className="ps-0"
                            scope="row"
                            style={{ width: "180px" }}
                          >
                            Code :
                          </th>
                          <td className="text-muted">{workOrder.code}</td>
                        </tr>
                      ) : null}
                      <tr>
                        <th className="ps-0" scope="row">
                          Title :
                        </th>
                        <td className="text-muted">
                          {workOrder.title?.trim() || "—"}
                        </td>
                      </tr>
                      <tr>
                        <th className="ps-0" scope="row">
                          Quotation :
                        </th>
                        <td className="text-muted">
                          {workOrder.source?.code && workOrder.sourceId ? (
                            <Link
                              to={`/quotations/${workOrder.sourceId}`}
                              className="link fw-medium"
                            >
                              {workOrder.source.code}
                            </Link>
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                      <tr>
                        <th className="ps-0" scope="row">
                          Client :
                        </th>
                        <td className="text-muted">
                          {workOrder.source?.client && workOrder.source.clientId ? (
                            <ClientPopover
                              client={workOrder.source.client}
                              clientId={workOrder.source.clientId}
                            >
                              <Link
                                to={`/clients/${workOrder.source.clientId}`}
                                className="link fw-medium"
                              >
                                {(() => {
                                  const name = getClientDisplayName(
                                    workOrder.source.client,
                                  );
                                  return name !== "—"
                                    ? name
                                    : workOrder.source.client.email || "—";
                                })()}
                              </Link>
                            </ClientPopover>
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                      <tr>
                        <th className="ps-0" scope="row">
                          Planned start :
                        </th>
                        <td className="text-muted">
                          {workOrder.plannedStartDate ? (
                            <DateOnly date={workOrder.plannedStartDate} />
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                      <tr>
                        <th className="ps-0" scope="row">
                          Planned end :
                        </th>
                        <td className="text-muted">
                          {workOrder.plannedEndDate ? (
                            <DateOnly date={workOrder.plannedEndDate} />
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                      <tr>
                        <th className="ps-0" scope="row">
                          Actual start :
                        </th>
                        <td className="text-muted">
                          {workOrder.actualStartDate ? (
                            <DateTime date={workOrder.actualStartDate} />
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                      <tr>
                        <th className="ps-0" scope="row">
                          Actual end :
                        </th>
                        <td className="text-muted">
                          {workOrder.actualEndDate ? (
                            <DateTime date={workOrder.actualEndDate} />
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                      <tr>
                        <th className="ps-0" scope="row">
                          Agreed amount :
                        </th>
                        <td className="text-muted">
                          {workOrder.agreedAmount != null
                            ? formatCurrency(workOrder.agreedAmount)
                            : "—"}
                        </td>
                      </tr>
                      {workOrder.finalAmount != null ? (
                        <tr>
                          <th className="ps-0" scope="row">
                            Final amount :
                          </th>
                          <td className="text-muted fw-medium">
                            {formatCurrency(workOrder.finalAmount)}
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </Table>
                </div>

                {workOrder.description?.trim() ? (
                  <div className="pt-3 border-top border-top-dashed mt-4">
                    <h6 className="mb-2 text-uppercase">Description</h6>
                    <p className="text-muted mb-0">{workOrder.description}</p>
                  </div>
                ) : null}
              </div>
            </CardBody>
          </Card>

          <Accordion
            open={lineItemsOpen}
            toggle={toggleLineItems}
            className="mb-4"
          >
            <AccordionItem>
              <AccordionHeader targetId="line-items">
                <span className="fw-medium">Line Items &amp; Costs</span>
              </AccordionHeader>
              <AccordionBody accordionId="line-items">
                <LineItemsTab />
              </AccordionBody>
            </AccordionItem>
          </Accordion>

          <FinancialSummaryCard
            workOrderId={workOrder.id}
            updatedAt={workOrder.updated_at}
            agreedAmount={workOrder.agreedAmount}
            finalAmount={workOrder.finalAmount}
          />
        </Col>
        <Col lg={4}>
          <WorkOrderTimeline workOrder={workOrder} />
          <div className="mb-3">
            <AssignmentsOverviewCard
              sourceType={WorkAssignmentSourceType.WORK_ORDER}
              sourceId={workOrder.id}
              sourceLabel={
                workOrder.code
                  ? `Work Order ${workOrder.code}`
                  : workOrder.title?.trim()
                    ? `Work Order: ${workOrder.title}`
                    : `Work Order #${workOrder.id}`
              }
              canAssign={canCreateAssignment && !isTerminalWorkOrderStatus(workOrder.status)}
              canReassign={canUpdateAssignment && !isTerminalWorkOrderStatus(workOrder.status)}
              onSuccess={() => void refetch()}
            />
          </div>
          <InvoiceCard
            workOrderId={workOrder.id}
            updatedAt={workOrder.updated_at}
          />
          <ClientAdvancesCard
            className="mt-3"
            sourceType={ClientAdvanceSourceType.WORK_ORDER}
            sourceId={workOrder.id}
          />
        </Col>
      </Row>
    </>
  );
}

const REVENUE_LABEL: Record<string, string> = {
  PROJECTED: "Agreed Amount",
  ACTUAL: "Final Billed",
  ESTIMATED: "Agreed Amount (estimated)",
};

const SUBCONTRACT_LABEL: Record<string, string> = {
  PROJECTED: "Agreed (Subcontract)",
  ACTUAL: "Final (Subcontract)",
  ESTIMATED: "Agreed (Subcontract)",
};

const MODE_BADGE_COLOR: Record<string, string> = {
  PROJECTED: "info",
  ACTUAL: "success",
  ESTIMATED: "warning",
};

const MODE_LABEL: Record<string, string> = {
  PROJECTED: "Projected",
  ACTUAL: "Actual",
  ESTIMATED: "Estimated",
};

function FinancialSummaryCard({
  workOrderId,
  updatedAt,
  agreedAmount,
  finalAmount,
}: {
  workOrderId: number;
  updatedAt: string;
  agreedAmount?: number | null;
  finalAmount?: number | null;
}) {
  const showSwitcher = agreedAmount != null && finalAmount != null;
  const [selectedMode, setSelectedMode] = useState<"PROJECTED" | "ACTUAL">(
    "PROJECTED",
  );
  const [data, setData] = useState<WorkOrderFinancials | null>(null);

  useEffect(() => {
    getWorkOrderFinancials(workOrderId, showSwitcher ? selectedMode : undefined)
      .then((res) => {
        if (res.success) setData(res.data);
      })
      .catch(() => {});
  }, [workOrderId, updatedAt, selectedMode, showSwitcher]);

  if (!data) return null;

  const hasCosts =
    data.itemsTotal > 0 || data.expenseTotal > 0 || data.subcontractTotal > 0;

  return (
    <Card>
      <CardBody>
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h6 className="text-uppercase mb-0">Financial Summary</h6>
          {showSwitcher ? (
            <div className="btn-group btn-group-sm" role="group">
              <button
                type="button"
                className={`btn ${selectedMode === "PROJECTED" ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => setSelectedMode("PROJECTED")}
              >
                Projected
              </button>
              <button
                type="button"
                className={`btn ${selectedMode === "ACTUAL" ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => setSelectedMode("ACTUAL")}
              >
                Actual
              </button>
            </div>
          ) : (
            <Badge
              color={MODE_BADGE_COLOR[data.mode] ?? "secondary"}
              className="fs-11"
            >
              {MODE_LABEL[data.mode] ?? data.mode}
            </Badge>
          )}
        </div>

        {data.mode === "ESTIMATED" && (
          <div className="alert alert-warning py-2 px-3 mb-3 small">
            Final billed amount not set — showing agreed amount as estimate
          </div>
        )}

        <Row className="gy-3 mb-0">
          {data.revenue > 0 && (
            <Col sm={12}>
              <p className="mb-1 text-uppercase fw-medium text-muted small">
                {REVENUE_LABEL[data.mode] ?? "Revenue"}
              </p>
              <h5 className="mb-0 fw-semibold">
                {formatCurrency(data.revenue)}
              </h5>
            </Col>
          )}

          <Col sm={12}>
            <p className="mb-1 text-uppercase fw-medium text-muted small">
              Total cost
            </p>
            <h5 className="mb-0 fw-semibold text-danger">
              {formatCurrency(data.totalCost)}
            </h5>
          </Col>

          {data.revenue > 0 && (
            <Col sm={12}>
              <p className="mb-1 text-uppercase fw-medium text-muted small">
                {data.isProfit ? "Profit" : "Loss"}
              </p>
              <h5
                className={`mb-0 fw-semibold ${data.isProfit ? "text-success" : "text-danger"}`}
              >
                {data.isProfit ? "+" : "-"}
                {formatCurrency(Math.abs(data.profit))}
              </h5>
            </Col>
          )}

          {data.revenue > 0 && (
            <Col sm={12}>
              <p className="mb-1 text-uppercase fw-medium text-muted small">
                Margin
              </p>
              <h5
                className={`mb-0 fw-semibold ${data.isProfit ? "text-success" : "text-danger"}`}
              >
                {data.margin.toFixed(2)}%
              </h5>
            </Col>
          )}
        </Row>

        {hasCosts && (
          <div className="pt-3 border-top border-top-dashed mt-3">
            <p className="text-uppercase fw-medium text-muted small mb-2">
              Cost breakdown
            </p>
            <div className="vstack gap-1">
              {data.itemsTotal > 0 && (
                <div className="d-flex justify-content-between">
                  <span className="text-muted small">Line items</span>
                  <span className="small fw-medium">
                    {formatCurrency(data.itemsTotal)}
                  </span>
                </div>
              )}
              {data.expenseTotal > 0 && (
                <div className="d-flex justify-content-between">
                  <span className="text-muted small">Direct expenses</span>
                  <span className="small fw-medium">
                    {formatCurrency(data.expenseTotal)}
                  </span>
                </div>
              )}
              {data.subcontractTotal > 0 && (
                <div className="d-flex justify-content-between">
                  <span className="text-muted small">
                    {SUBCONTRACT_LABEL[data.mode] ?? "Subcontracts"}
                  </span>
                  <span className="small fw-medium">
                    {formatCurrency(data.subcontractTotal)}
                  </span>
                </div>
              )}
              <div className="d-flex justify-content-between border-top pt-1 mt-1">
                <span className="small fw-semibold">Total</span>
                <span className="small fw-semibold text-danger">
                  {formatCurrency(data.totalCost)}
                </span>
              </div>
            </div>
          </div>
        )}

        {!hasCosts && data.revenue === 0 && (
          <p className="text-muted small mb-0 mt-2">
            No financial data recorded yet.
          </p>
        )}
      </CardBody>
    </Card>
  );
}

/**
 * Only IN_PROGRESS is genuine ongoing work — NEW (not started), ON_HOLD (paused),
 * and READY_FOR_BILLING (waiting in someone else's queue) are all resting states,
 * not active work, and should render as a static "done" dot.
 */
function resolveWorkOrderTimelineState(
  status: WorkOrderStatus,
  isLast: boolean,
): TimelineStepState {
  if (status === WorkOrderStatus.CANCELLED) return "skipped";
  if (isLast && status === WorkOrderStatus.IN_PROGRESS) return "active";
  return "done";
}

function buildWorkOrderTimeline(workOrder: WorkOrder): TimelineStep[] {
  const history = workOrder.statusHistory ?? [];

  return history.map((entry, i) => {
    const isLast = i === history.length - 1;
    const s = entry.status as WorkOrderStatus;
    const label = workOrderStatusMap[s]?.label ?? entry.status;
    const state = resolveWorkOrderTimelineState(s, isLast);

    const byLabel = entry.user
      ? [entry.user.firstName, entry.user.lastName].filter(Boolean).join(" ").trim() || `#${entry.by}`
      : entry.by != null
        ? `#${entry.by}`
        : undefined;

    return {
      label,
      timestamp: entry.at,
      by: byLabel,
      actor: entry.message ?? undefined,
      state,
      color: workOrderStatusMap[s]?.color,
    };
  });
}

function WorkOrderTimeline({ workOrder }: { workOrder: WorkOrder }) {
  return (
    <Card className="mb-3">
      <CardBody>
        <h6 className="text-uppercase mb-3">Timeline</h6>
        <Timeline steps={buildWorkOrderTimeline(workOrder)} />
      </CardBody>
    </Card>
  );
}

function InvoiceCard({
  workOrderId,
  updatedAt,
}: {
  workOrderId: number;
  /** Re-fetches whenever the work order changes — e.g. after regenerate-invoice,
   * which may replace the active invoice or correct it in place. */
  updatedAt?: string;
}) {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getInvoices({
      sourceType: InvoiceSourceType.WORK_ORDER,
      sourceId: workOrderId,
      limit: 10,
    })
      .then((res) => {
        if (res.success) setInvoices(res.data.rows);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [workOrderId, updatedAt]);

  if (loading) {
    return (
      <Card className="mt-3">
        <CardBody className="text-center py-3">
          <Spinner size="sm" />
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="mt-3">
      <CardBody>
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h6 className="text-uppercase mb-0">Invoices</h6>
          <span className="badge bg-light text-dark">{invoices.length}</span>
        </div>

        {invoices.length === 0 ? (
          <p className="text-muted small mb-0">No invoices raised yet.</p>
        ) : (
          <div className="vstack gap-2">
            {invoices.map((inv) => {
              const statusInfo = InvoiceStatusMap[inv.status];
              return (
                <div
                  key={inv.id}
                  className="d-flex align-items-center justify-content-between"
                >
                  <div className="d-flex flex-column gap-1">
                    <Link
                      to={`/finance/invoices/${inv.id}`}
                      className="fw-medium text-body text-decoration-none link"
                    >
                      {inv.code || `Invoice #${inv.id}`}
                    </Link>
                    {inv.issueDate ? (
                      <span className="text-muted small">
                        Issued: <DateOnly date={inv.issueDate} />
                      </span>
                    ) : null}
                    {inv.status === InvoiceStatus.CANCELLED &&
                    inv.cancelReason ? (
                      <span className="text-muted small">
                        Reason: {inv.cancelReason}
                      </span>
                    ) : null}
                  </div>
                  <div className="d-flex flex-column align-items-end gap-1">
                    <Badge
                      color={statusInfo?.color ?? "secondary"}
                      className="fs-11"
                    >
                      {statusInfo?.label ?? inv.status}
                    </Badge>
                    {inv.status === InvoiceStatus.CANCELLED &&
                    inv.cancelledAt ? (
                      <span className="text-muted small">
                        <DateOnly date={inv.cancelledAt} />
                      </span>
                    ) : null}
                    {inv.outstandingAmount > 0 ? (
                      <span className="text-muted small">
                        Due: {formatCurrency(inv.outstandingAmount)}
                      </span>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardBody>
    </Card>
  );
}
